import { useCallback, useState, type ReactNode } from 'react';
import { CaretDown, CaretRight, Check, Copy } from '@phosphor-icons/react';
import { JsonView, collapseAllNested, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import {
  TONE_CLASS,
  jsonSize,
  type SignalTone,
} from '../../lib/admin-conversations';

export function Chip({
  tone,
  children,
  title,
  onClick,
}: {
  tone: SignalTone;
  children: ReactNode;
  title?: string;
  onClick?: () => void;
}) {
  const className = `inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-extrabold leading-none ${TONE_CLASS[tone]}`;
  if (!onClick) {
    return (
      <span className={className} title={title}>
        {children}
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} title={title} className={`${className} cursor-pointer hover:opacity-80 transition-opacity`}>
      {children}
    </button>
  );
}

export function CopyButton({ value, label = 'Copier' }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setDone(true);
        setTimeout(() => setDone(false), 1400);
      })
      .catch(() => {});
  }, [value]);
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      title={label}
      className="inline-flex items-center gap-1 shrink-0 min-h-[44px] sm:min-h-[28px] px-2 rounded-[var(--naia-r-sm)] border border-[var(--naia-line)] text-[11px] font-semibold text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] hover:border-[var(--naia-blue)] transition-colors"
    >
      {done ? <Check size={12} weight="bold" /> : <Copy size={12} />}
      <span>{done ? 'Copié' : label}</span>
    </button>
  );
}

export function IdRow({ label, value, extra }: { label: string; value: string | null; extra?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-[5px] border-b border-[var(--naia-line-soft)] last:border-b-0">
      <span className="w-[92px] shrink-0 text-[10.5px] font-black uppercase tracking-[0.6px] text-[var(--naia-text-secondary)]">
        {label}
      </span>
      {value ? (
        <>
          <code className="font-[var(--naia-font-mono)] text-[12px] text-[var(--naia-text-primary)] break-all">{value}</code>
          <CopyButton value={value} />
        </>
      ) : (
        <span className="text-[12px] text-[var(--naia-text-secondary)] opacity-70">—</span>
      )}
      {extra}
    </div>
  );
}

/* Un bloc JSON : arbre replié par défaut pour lire la forme, bascule « brut »
   pour lire et copier la valeur exacte. La valeur n'est jamais tronquée — seule
   la hauteur est bornée, avec un défilement interne. */
export function JsonBlock({
  label,
  value,
  defaultOpen = false,
  tone = 'neutral',
}: {
  label: string;
  value: unknown;
  defaultOpen?: boolean;
  tone?: SignalTone;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [raw, setRaw] = useState(false);
  const [tall, setTall] = useState(false);
  const size = jsonSize(value);
  const text = value === null || value === undefined ? '' : JSON.stringify(value, null, 2);

  return (
    <div className="rounded-[var(--naia-r-sm)] border border-[var(--naia-line)] overflow-hidden">
      <div className="flex items-center gap-2 flex-wrap px-2 py-[5px] bg-[var(--naia-card-alt)]">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="inline-flex items-center gap-1 min-h-[44px] sm:min-h-[28px] text-[11px] font-black uppercase tracking-[0.6px] text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] transition-colors"
        >
          {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
          {label}
        </button>
        <span className={`text-[10.5px] font-bold rounded px-1.5 py-[1px] ${TONE_CLASS[tone]}`}>
          {size.toLocaleString('fr-FR')} car.
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {open && (
            <button
              type="button"
              onClick={() => setRaw((v) => !v)}
              className="min-h-[44px] sm:min-h-[28px] px-2 rounded-[var(--naia-r-sm)] border border-[var(--naia-line)] text-[11px] font-semibold text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] hover:border-[var(--naia-blue)] transition-colors"
            >
              {raw ? 'Arbre' : 'Brut'}
            </button>
          )}
          <CopyButton value={text} />
        </div>
      </div>
      {open && (
        <div
          className={`overflow-auto bg-[var(--naia-card)] px-2 py-2 text-[12px] ${tall ? 'max-h-[70vh]' : 'max-h-[300px]'}`}
        >
          {raw ? (
            <pre className="font-[var(--naia-font-mono)] text-[11.5px] leading-[1.5] text-[var(--naia-text-primary)] whitespace-pre">
              {text || '—'}
            </pre>
          ) : (
            <JsonView data={value as object} shouldExpandNode={collapseAllNested} style={defaultStyles} />
          )}
        </div>
      )}
      {open && size > 900 && (
        <button
          type="button"
          onClick={() => setTall((v) => !v)}
          className="w-full min-h-[44px] sm:min-h-[32px] text-[11px] font-semibold text-[var(--naia-text-secondary)] hover:text-[var(--naia-blue)] bg-[var(--naia-card-alt)] border-t border-[var(--naia-line)] transition-colors"
        >
          {tall ? 'Réduire la hauteur' : 'Agrandir'}
        </button>
      )}
    </div>
  );
}
